import React, { useEffect, useState } from "react";
import { Modal, Button, Typography, Alert, Input } from "antd";
import { X, Lock, CircleCheck } from "lucide-react";
import Icon from "./Icon";
import { changePassword } from "../auth/auth.service";

const { Text } = Typography;

const ChangePasswordDialog = ({ open, onClose }) => {
  const [success, setSuccess] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  // Reset state when open dialog
  useEffect(() => {
    if (open) {
      setCurrentPasswordError("");
      setNewPasswordError("");
      setConfirmPasswordError("");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSuccess(false);
    }
  }, [open]);

  const handleSaveChange = async () => {
    let error = false;

    if (!currentPassword || currentPassword.length < 6) {
      setCurrentPasswordError("Password must be at least 6 characters long.");
      error = true;
    }
    if (!newPassword || newPassword.length < 6) {
      setNewPasswordError("Password must be at least 6 characters long.");
      error = true;
    }
    if (!confirmPassword || confirmPassword.length < 6) {
      setConfirmPasswordError("Password must be at least 6 characters long.");
      error = true;
    }
    if (newPassword !== confirmPassword) {
      setConfirmPasswordError("Password doesn't match.");
      error = true;
    }
    if (error) return;

    // Call service
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess(true);
    } catch (error) {
      setCurrentPasswordError("Password Incorrect");
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setCurrentPassword(value);

    if (currentPasswordError !== "" && value) setCurrentPasswordError("");
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);

    if (newPasswordError !== "" && value) setNewPasswordError("");
  };

  const handleConfirmPassword = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (confirmPasswordError !== "" && value) setConfirmPasswordError("");
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon as={Lock} style={{ color: "var(--ant-color-primary)" }} />
          <span>Change Password</span>
        </div>
      }
      className="airsmart-dialog"
      footer={
        !success ? (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" onClick={handleSaveChange}>
              Save
            </Button>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="primary" onClick={onClose}>
              Close
            </Button>
          </div>
        )
      }
      width={576}
      closeIcon={<Icon as={X} />}
      destroyOnClose
    >
      {!success ? (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
              Current Password
            </Text>
            <Input.Password
              status={currentPasswordError ? "error" : ""}
              value={currentPassword}
              onChange={handlePasswordChange}
              placeholder="Enter your current password"
            />
            {currentPasswordError && (
              <Text type="danger" style={{ fontSize: 12 }}>
                {currentPasswordError}
              </Text>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
              New Password
            </Text>
            <Input.Password
              status={newPasswordError ? "error" : ""}
              value={newPassword}
              onChange={handleNewPasswordChange}
              placeholder="Enter your new password"
            />
            {newPasswordError && (
              <Text type="danger" style={{ fontSize: 12 }}>
                {newPasswordError}
              </Text>
            )}
          </div>

          <div style={{ marginBottom: 24 }}>
            <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
              Confirm New Password
            </Text>
            <Input.Password
              status={confirmPasswordError ? "error" : ""}
              value={confirmPassword}
              onChange={handleConfirmPassword}
              placeholder="Confirm your new password"
            />
            {confirmPasswordError && (
              <Text type="danger" style={{ fontSize: 12 }}>
                {confirmPasswordError}
              </Text>
            )}
          </div>

          <Alert
            type="info"
            showIcon
            message="Password must be at least 6 characters long."
            style={{ marginTop: 16 }}
          />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "32px 24px",
          }}
        >
          <Icon
            as={CircleCheck}
            size={48}
            strokeWidth={2.5}
            style={{
              color: "var(--ant-color-success)",
              marginBottom: 16,
            }}
          />
          <Text
            strong
            style={{
              color: "var(--ant-color-success)",
              textAlign: "center",
              wordBreak: "break-word",
              marginBottom: 8,
              display: "block",
            }}
          >
            Password Updated Successfully
          </Text>
          <Text
            type="secondary"
            style={{ textAlign: "center", padding: "0 16px" }}
          >
            Your password has been changed successfully. You can now use your new
            password to sign in.
          </Text>
        </div>
      )}
    </Modal>
  );
};

export default ChangePasswordDialog;
